import {
	getProductsFilterSchema,
	type ProductsSortableField,
	type ProductsSortOrder,
} from "@dransay/api/models";
import isEqual from "lodash/isEqual";
import throttle from "lodash/throttle";
import { parseAsJson, useQueryState } from "nuqs";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useFetcher } from "react-router";
import { useInfiniteScrollCache } from "~/contexts/infinite-scroll-cache.context";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import { useOrderQuery } from "~/features/products/use-order-query";
import { useSortQuery } from "~/features/products/use-sort-query";

interface InfiniteScrollProps<T> {
	renderItem: (item: T, index: number) => React.ReactNode;
	endpoint: string; // Endpoint to fetch data
	pageSize?: number; // Number of items per page
	containerClassName?: string;
	itemsProperty?: string;
	renderEmptyState?: () => React.ReactNode;
	renderLoadingState?: () => React.ReactNode;
	onCountChange?: (count: number) => void;
	initialItems?: T[];
	threshold?: number; // Distance from bottom to trigger load
	onItemsLoaded?: (
		items: T[],
		params: {
			filters: ReturnType<
				ReturnType<typeof getProductsFilterSchema>["parse"]
			> | null;
			sort: ProductsSortableField | null;
			order: ProductsSortOrder | null;
			search: string | null;
		},
		startPosition: number,
	) => void;
	/** Notifies parent when the list is clearing/reloading after query change (e.g. filters). */
	onReloadingChange?: (isReloading: boolean) => void;
}

export const InfiniteScroll = <T extends { id: string | number }>({
	renderItem,
	endpoint,
	pageSize = 10,
	containerClassName,
	itemsProperty = "items",
	renderEmptyState = renderEmptyStateFallback,
	renderLoadingState = renderLoadingStateFallback,
	onCountChange,
	initialItems = [],
	threshold = 600,
	onItemsLoaded,
	onReloadingChange,
}: InfiniteScrollProps<T>) => {
	const [sort] = useSortQuery();
	const [order] = useOrderQuery();
	const [filters] = useQueryState(
		"filters",
		parseAsJson(getProductsFilterSchema().parse),
	);
	const [search] = useQueryState("search");

	const { selectedVendorId, selectedDeliveryMethodKind } = useVendorSelection();
	const vendorFilters = [
		selectedVendorId,
		selectedDeliveryMethodKind,
	].toString();

	// The list state is cached to avoid re-fetching data when the user navigates back
	const { getCache, setCache } = useInfiniteScrollCache();

	const querySignature = JSON.stringify({
		sort,
		order,
		filters,
		search,
		vendorFilters,
	});

	const cachedData = getCache(endpoint);
	const isValidCache = cachedData?.querySignature === querySignature;

	const [items, setItems] = useState<T[]>(
		isValidCache ? cachedData.items : initialItems,
	);
	const [page, setPage] = useState(0);
	const [hasMore, setHasMore] = useState(
		isValidCache ? cachedData.hasMore : true,
	);
	const [isReloading, setIsReloading] = useState(false);
	const fetchedPages = useRef<Set<number>>(
		isValidCache
			? cachedData.fetchedPages
			: new Set(initialItems.length ? [0] : []),
	);
	// Items present at the initial render (SSR markup or restored back-nav cache)
	// must NOT replay the entrance animation. `animate-in fade-in` ramps opacity
	// 0 -> 1 on every mount, so applying it to the server-rendered cards made the
	// whole grid visibly fade in from transparent on every page refresh. Only
	// client-appended items (index >= this boundary) animate.
	const initialItemCountRef = useRef(
		isValidCache ? cachedData.items.length : initialItems.length,
	);
	const containerRef = useRef<HTMLDivElement>(null);
	const isInitialMount = useRef(true);

	const fetcher = useFetcher();

	useEffect(() => {
		onReloadingChange?.(isReloading);
	}, [isReloading, onReloadingChange]);

	const checkQueryChange = useQueryChange(
		sort,
		order,
		JSON.stringify(filters),
		search,
		vendorFilters,
	);

	const loadPage = async (pageToLoad: number, force = false) => {
		const skip =
			fetchedPages.current.has(pageToLoad) ||
			(!force && fetcher.state === "loading");

		if (skip) return;

		const params = new URLSearchParams();

		params.set("page", pageToLoad.toString());
		params.set("pageSize", pageSize.toString());
		params.set("sort", sort?.toString() ?? "");
		params.set("order", order?.toString() ?? "");

		const serializedFilters =
			typeof filters === "object" ? JSON.stringify(filters) : filters;
		params.set("filters", serializedFilters?.toString() ?? "");

		params.set("vendorId", selectedVendorId?.toString() ?? "");
		params.set("deliveryMethod", selectedDeliveryMethodKind?.toString() ?? "");
		params.set("search", search ?? "");
		await fetcher.load(`${endpoint}?${params.toString()}`);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies:
	useEffect(() => {
		if (fetcher.data && !fetchedPages.current.has(fetcher.data.page)) {
			const {
				[itemsProperty]: newItems,
				hasMore: newHasMore,
				page: newPage,
				count,
			} = fetcher.data;

			// Error responses such as the rate limiter's 429 carry no items array,
			// so they must not be merged into the list or marked as fetched.
			if (!Array.isArray(newItems)) {
				setIsReloading(false);
				return;
			}

			const updatedItems = [...items, ...newItems];
			setItems(updatedItems);
			setHasMore(newHasMore);
			onCountChange?.(count);
			fetchedPages.current.add(newPage);
			setIsReloading(false);

			// Update cache with current scroll position
			setCache(endpoint, {
				items: updatedItems,
				fetchedPages: fetchedPages.current,
				hasMore: newHasMore,
				count,
				querySignature,
				scrollPosition: window.scrollY,
			});

			onItemsLoaded?.(newItems, { filters, search, sort, order }, items.length);
		}
	}, [fetcher.data, endpoint, querySignature]);

	// biome-ignore lint/correctness/useExhaustiveDependencies:
	const handleScroll = useCallback(
		throttle(() => {
			const el = containerRef.current;

			let remaining: number;

			if (el) {
				// Element is not its own scroll container, so derive relative scroll from window
				const elementHeight = el.scrollHeight; // total content height
				const viewportHeight = window.innerHeight;
				const topOffset = el.offsetTop; // distance from document top
				// How far we've scrolled inside the element (can be negative before element enters view)
				const rawRelativeScroll = window.scrollY - topOffset;
				const relativeScroll = Math.max(0, rawRelativeScroll); // clamp before top
				// Portion of element that is now visible bottom edge wise
				remaining = Math.max(
					0,
					elementHeight - (relativeScroll + viewportHeight),
				);
			} else {
				// Fallback to full document scroll if ref not ready
				const scrollTop = window.scrollY;
				const viewportHeight = window.innerHeight;
				const scrollHeight = document.documentElement.scrollHeight;
				remaining = Math.max(0, scrollHeight - (scrollTop + viewportHeight));
			}

			if (fetcher.state === "idle" && hasMore && remaining <= threshold) {
				setPage((prevPage) => prevPage + 1);
			}

			// Cache uses window scroll position since container isn't independently scrollable
			const currentCache = getCache(endpoint);
			if (currentCache) {
				setCache(endpoint, {
					...currentCache,
					scrollPosition: window.scrollY,
				});
			}
		}, 150),
		[fetcher.state, hasMore, threshold],
	);

	useEffect(() => {
		window.addEventListener("scroll", handleScroll);
		return () => {
			handleScroll.cancel();
			window.removeEventListener("scroll", handleScroll);
		};
	}, [handleScroll]);

	// Restore scroll position after initial render if cache is valid
	// biome-ignore lint/correctness/useExhaustiveDependencies: only initial load
	useEffect(() => {
		if (isValidCache && cachedData.scrollPosition) {
			window.scrollTo(0, cachedData.scrollPosition);
		}
	}, []);

	useEffect(() => {
		onItemsLoaded?.(items, { filters, search, sort, order }, 0);
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies:
	useEffect(() => {
		void loadPage(page);
	}, [page]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: sort and filters trigger reset
	useEffect(() => {
		// Skip the initial mount to prevent race conditions with vendor selection context
		// The initial products are already loaded from the loader data
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		if (!checkQueryChange()) return;

		setIsReloading(true);
		fetchedPages.current = new Set();
		// A new query replaces the SSR set entirely; let its fresh results animate
		// in (they arrive after a skeleton reload, where the fade reads as intended).
		initialItemCountRef.current = 0;
		setItems([]);
		setPage(0);
		setHasMore(false);

		// Reset scroll position only for new queries
		window.scrollTo(0, 0);
		void loadPage(0, true);
	}, [sort, order, search, filters, vendorFilters]);

	// Determine what content to render inside the container
	const renderContent = () => {
		if (isReloading) {
			return renderLoadingState();
		}
		if (!items.length && fetcher.state === "idle") {
			return renderEmptyState();
		}
		return (
			<>
				{items.map((item, index) => {
					const shouldAnimate = index >= initialItemCountRef.current;
					return (
						<Fragment key={item.id}>
							<div
								data-testid={`infinite-scroll-item-${index}`}
								className={
									shouldAnimate
										? "transition-opacity duration-300 animate-in fade-in"
										: undefined
								}
							>
								{renderItem(item, index)}
							</div>
						</Fragment>
					);
				})}
				{hasMore && <div style={{ height: "1px" }} />}
			</>
		);
	};

	const hasItems = items.length > 0;

	// Always render the container to maintain DOM stability during filter changes
	// This prevents test flakiness where elements temporarily disappear
	// Apply grid/layout classes only when items exist to avoid empty space on no-results
	return (
		<div
			ref={containerRef}
			className={`transition-opacity duration-300 ${isReloading ? "opacity-50" : "opacity-100"} ${hasItems || isReloading ? containerClassName : ""}`}
			data-testid="infinite-scroll-container"
		>
			{renderContent()}
		</div>
	);
};

const renderEmptyStateFallback = () => (
	<div className="text-gray-500">No items</div>
);

const renderLoadingStateFallback = () => (
	<div className="text-gray-500">Loading...</div>
);

function useQueryChange<T>(
	sort: T,
	order: T,
	filters: T,
	search: T,
	vendorFilters: T,
) {
	const previousSort = useRef(sort);
	const previousOrder = useRef(order);
	const previousFilters = useRef(filters);
	const previousSearch = useRef(search);
	const previousVendorFilters = useRef(vendorFilters);

	const hasChanged = () => {
		const sortChanged = !isEqual(sort, previousSort.current);
		const orderChanged = !isEqual(order, previousOrder.current);
		const filtersChanged = !isEqual(filters, previousFilters.current);
		const searchChanged = !isEqual(search, previousSearch.current);
		const vendorFiltersChanged = !isEqual(
			vendorFilters,
			previousVendorFilters.current,
		);

		if (
			sortChanged ||
			orderChanged ||
			filtersChanged ||
			searchChanged ||
			vendorFiltersChanged
		) {
			previousSort.current = sort;
			previousOrder.current = order;
			previousFilters.current = filters;
			previousSearch.current = search;
			previousVendorFilters.current = vendorFilters;
			return true;
		}

		return false;
	};

	return hasChanged;
}
